import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Box } from "@mui/material";
import "./styles/ContentItem.scss";
import ScriptBox from "../../../components/ScriptBox/ScriptBox";
import SimpleScriptBox from "../../../views/ProjectSheet/SimpleScriptBox/SimpleScriptBox";
import AttachmentIcon from "../../../components/VectorIcons/AttachmentIcon";
import ModalHeader from "../../../views/Activities/NewActivity/ModalHeader/ModalHeader";
import AddAttachments from "../../../views/Activities/NewActivity/AddAttachments/AddAttachments";
import CreateActivity from "../../../views/Activities/NewActivity/CreateActivity/CreateActivity";
import ContentRow from "../../../views/Activities/ContentRow/ContentRow";
import AttachmentRow from "../../../views/Activities/AttachmentRow/AttachmentRow";
import AttachmentContext from "../../../context/attachmentsContext";
import ContentActivityFooter from "../../../views/Activities/NewActivity/ContentActivityFooter/ContentActivityFooter";
import { countSelectedAttachments } from "../../../utils";
import { useAttachmentSelection } from "../../../hooks/useAttachmentSelection";

const separateParentAndVersions = (files) =>
  files.map((file) => ({
    parent: file[0],
    version: file.filter((e) => e !== file[0]),
  }));

const ContentItem = ({
  appContext,
  currentProject,
  currentFiles,
  type,
  history,
  index,
  getFiles,
  setLoading,
  contentType,
  onSelectionChange,
  isDisabled,
  hasGlobalSelection,
  clearSelectionsTrigger,
  onActivityCreated,
}) => {
  const [stateSelectFiles, setStateSelectFiles] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const attachmentContext = useContext(AttachmentContext);

  const {
    toggleActiveCheckBox: toggleSelectionParent,
    toggleActiveVersionCheckBox: toggleSelectionVersion,
    clearActive,
    applySelection,
  } = useAttachmentSelection();

  const chaptersOrEpisodes = useMemo(() => {
    const types = { Dramaturgia: "Capítulo/Episódio", Variedades: "Episódio" };
    return types[contentType];
  }, [contentType]);

  const compiledFiles = useMemo(() => {
    if (!currentFiles?.length) return [];

    const files = currentFiles.flatMap((file) => [
      file.attachment,
      ...(file.olderVersions ?? []),
    ]);

    const allGroup = attachmentContext.compileAll(
      files,
      chaptersOrEpisodes,
      true
    );
    const filteredAllGroup = Object.fromEntries(
      Object.entries(allGroup).map(([key, groupFiles]) => [
        key,
        groupFiles.filter((file) => file.fileId && file.fileName),
      ])
    );
    const chapterCompiled = attachmentContext.compiledChapter(
      files,
      type,
      chaptersOrEpisodes
    );

    const addendumCompiled = attachmentContext.compileAddendum(files);

    const otherGroups = Object.values({
      ...filteredAllGroup,
      ...addendumCompiled,
    }).sort((a, b) => {
      const aName = (a[0]?.attachmentType ?? a[0]?.type)?.name ?? "";
      const bName = (b[0]?.attachmentType ?? b[0]?.type)?.name ?? "";
      return aName.localeCompare(bName);
    });

    const mergeGroups = [
      ...Object.entries(chapterCompiled),
      ...otherGroups.map((group, i) => [i, group]),
    ];

    return separateParentAndVersions(mergeGroups.map((f) => f[1]));
  }, [currentFiles, attachmentContext, chaptersOrEpisodes, type]);

  const stateFiles = useMemo(
    () => applySelection(compiledFiles),
    [compiledFiles, applySelection]
  );

  const toggleOpenList = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const toggleActiveCheckBox = useCallback(
    (file, index) => {
      if (isDisabled) return;
      toggleSelectionParent(file, index);
    },
    [isDisabled, toggleSelectionParent]
  );

  const toggleActiveVersionCheckBox = useCallback(
    (item, index, versionIndex) => {
      if (isDisabled) return;
      toggleSelectionVersion(item, index, versionIndex);
    },
    [isDisabled, toggleSelectionVersion]
  );

  const createIsValid = useCallback(() => {
    if (!currentProject) return false;
    const hasVersions = currentProject.type !== "Pesquisa";
    const parentIsValid =
      stateFiles.length > 0 &&
      stateFiles.some((item) => !!item.parent?.isActive);
    const versionIsValid = hasVersions
      ? stateFiles.length > 0 &&
        stateFiles.some((item) => item.version?.some((v) => !!v?.isActive))
      : false;
    return parentIsValid || versionIsValid;
  }, [currentProject, stateFiles]);

  useEffect(() => {
    if (clearSelectionsTrigger > 0) clearActive();
  }, [clearSelectionsTrigger, clearActive]);

  useEffect(() => {
    clearActive();
  }, [currentFiles, clearActive]);

  useEffect(() => {
    if (!Array.isArray(stateFiles) || !onSelectionChange) return;
    const hasAnyActive = stateFiles.some(
      (item) => item.parent?.isActive || item.version?.some((v) => v?.isActive)
    );
    onSelectionChange(
      index,
      hasAnyActive,
      createIsValid(),
      countSelectedAttachments(stateFiles)
    );
  }, [stateFiles, onSelectionChange, index, createIsValid]);

  if (!currentProject) return null;

  const isResearch = currentProject.type === "Pesquisa";
  const isValid = createIsValid();
  const selectedCount = countSelectedAttachments(stateFiles);
  const formattedLastAttachment = appContext.formatDate(
    appContext.convertDatePtBr(currentProject.lastAttachmentReceivedDate)
  );
  const sheetLink = appContext.redirectToSheet(contentType, currentProject.id);
  const createActivityStep = `createProjectActivity_${index}`;
  const detailsText = isResearch
    ? `${currentProject.researchCategory} • ${currentProject.researchSubCategory}`
    : `${currentProject.format}${currentProject.format === "Novela" ? ` • ${currentProject.soapOperaSchedule}` : ""}`;
  const visibleFiles = stateFiles.filter(
    (file) => file?.parent?.fileId && file?.parent?.fileName
  );

  return (
    <>
      <ContentRow
        iconType={currentProject.contentType}
        title={currentProject.title}
        people={
          isResearch
            ? currentProject.researchers
            : currentProject.creators.map((creator) => creator.name)
        }
        detailsText={detailsText}
        origins={currentProject.origins ?? []}
        lastAttachmentText={formattedLastAttachment}
        sheetLink={sheetLink}
        expanded={isOpen}
        onToggle={toggleOpenList}
        disabled={isDisabled}
      >
        <Box className="attachmentsList">
          <Box component="ul" className="attachmentsHeader">
            <Box component="li" className="type">
              Tipo
            </Box>
            <Box component="li" className="version">
              Versão
            </Box>
            <Box component="li" className="file">
              Arquivo
            </Box>
            <Box component="li" className="receivedAt">
              Recebido Em
            </Box>
            <Box component="li" />
          </Box>

          {visibleFiles.map((file, fileIndex) => (
            <AttachmentRow
              key={fileIndex}
              file={file}
              index={fileIndex}
              format={currentProject.format}
              onToggleParent={toggleActiveCheckBox}
              onToggleVersion={toggleActiveVersionCheckBox}
            />
          ))}
        </Box>

        {!hasGlobalSelection && (
          <ContentActivityFooter
            isValid={isValid}
            selectedCount={selectedCount}
            onCreateActivity={() =>
              appContext.setCurrentStepBox(createActivityStep)
            }
            onCancel={clearActive}
          />
        )}
      </ContentRow>

      {stateSelectFiles && (
        <SimpleScriptBox
          open={stateSelectFiles}
          title="Selecionar anexos"
          icon={<AttachmentIcon />}
          removePadding={true}
          resizeHeader={true}
          toggleScriptBox={() => setStateSelectFiles(false)}
        >
          <AddAttachments
            files={stateFiles}
            toggleActiveCheckBox={toggleActiveCheckBox}
            toggleActiveVersionCheckBox={toggleActiveVersionCheckBox}
            clearActive={clearActive}
            closedModal={() => setStateSelectFiles(false)}
          />
        </SimpleScriptBox>
      )}

      {appContext.current.step === createActivityStep && (
        <ScriptBox
          open={appContext.current.step === createActivityStep}
          icon={
            <ModalHeader
              title={currentProject.title}
              creators={currentProject.creators}
              type={currentProject.contentType}
              format={currentProject.format}
            />
          }
          resizeHeader={true}
          removePadding={true}
          marginBottom={true}
        >
          <CreateActivity
            appContext={appContext}
            files={stateFiles}
            toggleActiveCheckBox={toggleActiveCheckBox}
            toggleActiveVersionCheckBox={toggleActiveVersionCheckBox}
            listAttachments={() => setStateSelectFiles(true)}
            clearActive={clearActive}
            type={type}
            history={history}
            currentProject={currentProject.id}
            projectType={currentProject.contentType}
            format={currentProject.format}
            getFiles={getFiles}
            setLoading={setLoading}
            onSuccess={onActivityCreated}
          />
        </ScriptBox>
      )}
    </>
  );
};

export default ContentItem;
